
import { Component, ChangeDetectionStrategy, inject, signal, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DataService } from '../../services/data.service';
import { AuthService } from '../../services/auth.service';
// FIX: Corrected import path for models.
import { Product, Customer } from '../../models/index';
import { isSupabaseConfigured } from '../../firebase';

@Component({
  selector: 'app-data-importer',
  standalone: true,
  templateUrl: './data-importer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule]
})
export class DataImporterComponent implements OnInit {
  private dataService = inject(DataService);
  // FIX: Explicitly typed the injected `DOCUMENT` to resolve "Property 'defaultView' does not exist on type 'unknown'".
  private document: Document = inject(DOCUMENT);
  // FIX: Inject AuthService to get session data like storeId.
  private authService = inject(AuthService);

  // Signals for text area content
  productsCsv = signal('');
  customersCsv = signal('');

  // Signals for status and loading states
  isSupabaseMode = signal(false);
  productStatus = signal<{ type: 'success' | 'error', message: string } | null>(null);
  customerStatus = signal<{ type: 'success' | 'error', message: string } | null>(null);
  isImportingProducts = signal(false);
  isImportingCustomers = signal(false);

  ngOnInit(): void {
    this.isSupabaseMode.set(isSupabaseConfigured());
  }

  reloadPage(): void {
    this.document.defaultView?.location.reload();
  }

  /**
   * Helper to parse a CSV line handling quoted fields correctly.
   * e.g. "Product, Name", 100 -> ["Product, Name", "100"]
   */
  private parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let startValueIndex = 0;
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        let val = line.substring(startValueIndex, i).trim();
        // Remove surrounding quotes if present
        if (val.startsWith('"') && val.endsWith('"')) {
            val = val.substring(1, val.length - 1);
        }
        result.push(val);
        startValueIndex = i + 1;
      }
    }
    // Add last value
    let lastVal = line.substring(startValueIndex).trim();
    if (lastVal.startsWith('"') && lastVal.endsWith('"')) {
        lastVal = lastVal.substring(1, lastVal.length - 1);
    }
    result.push(lastVal);
    
    return result;
  }

  async importProducts(): Promise<void> {
    if (!this.productsCsv().trim()) {
      this.productStatus.set({ type: 'error', message: 'Text area is empty.' });
      return;
    }
    if (!confirm('WARNING: This will ERASE all existing products and replace them with the new data. Are you sure you want to continue?')) {
        return;
    }

    this.isImportingProducts.set(true);
    this.productStatus.set(null);

    try {
      // FIX: Get storeId from the current user's session.
      const storeId = this.authService.currentUser()?.storeId;
      if (!storeId) {
        throw new Error('Could not determine store ID. Please ensure you are logged in.');
      }
      
      const lines = this.productsCsv().trim().split('\n');
      const headerLine = lines.shift();
      
      if (!headerLine) throw new Error('CSV is empty');
      
      const header = this.parseCsvLine(headerLine).map(h => h.toLowerCase().trim());
      
      const requiredHeaders = ['sku', 'name', 'category', 'price', 'stock'];
      if (!requiredHeaders.every(h => header.includes(h))) {
        throw new Error(`Invalid headers. Required headers are: ${requiredHeaders.join(', ')}`);
      }

      // NOTE: This importer is simplified and does not map to existing categories.
      // It assumes a fresh import where category names will be used directly.
      // A more advanced version would fetch categories and map by name.
      const productsToImport: Omit<Product, 'id' | 'createdAt'>[] = lines
        .filter(l => l.trim().length > 0)
        .map(line => {
            const values = this.parseCsvLine(line);
            const row = header.reduce((obj, col, index) => {
                obj[col] = values[index]?.trim() ?? '';
                return obj;
            }, {} as any);

            return {
                storeId,
                sku: row.sku,
                name: row.name,
                categoryId: '', // Importer cannot know the ID, backend should handle name->ID mapping if needed
                categoryName: row.category,
                isPrescriptionRequired: false,
                isComposite: false,
                isTaxable: row.istaxable ? row.istaxable.toLowerCase() === 'true' : true,
                price: parseFloat(row.price) || 0,
                cost: parseFloat(row.cost) || 0,
                stockQuantity: parseInt(row.stock, 10) || 0,
                unit: row.unit?.toUpperCase() === 'PIECE' ? 'PIECE' : 'GRAM',
                imageUrl: `https://picsum.photos/seed/${row.sku}/200/200`,
                active: row.active ? row.active.toLowerCase() === 'true' : true,
            };
      });
      
      const result = await this.dataService.batchAddProducts(productsToImport);
      if (result.success) {
        this.productStatus.set({ type: 'success', message: result.message });
        this.productsCsv.set('');
      } else {
        this.productStatus.set({ type: 'error', message: `Import failed: ${result.message}` });
      }

    } catch (e) {
      const message = e instanceof Error ? e.message : 'An unknown error occurred';
      this.productStatus.set({ type: 'error', message });
    } finally {
      this.isImportingProducts.set(false);
    }
  }

  async importCustomers(): Promise<void> {
     if (!this.customersCsv().trim()) {
      this.customerStatus.set({ type: 'error', message: 'Text area is empty.' });
      return;
    }
    if (!confirm('WARNING: This will ERASE all existing customers and replace them with the new data. Are you sure you want to continue?')) {
        return;
    }

    this.isImportingCustomers.set(true);
    this.customerStatus.set(null);

    try {
      // FIX: Get storeId from the current user's session.
      const storeId = this.authService.currentUser()?.storeId;
      if (!storeId) {
        throw new Error('Could not determine store ID. Please ensure you are logged in.');
      }
      
      const lines = this.customersCsv().trim().split('\n');
      const headerLine = lines.shift();
      
      if (!headerLine) throw new Error('CSV is empty');

      const header = this.parseCsvLine(headerLine).map(h => h.toLowerCase().trim());

      const requiredHeaders = ['name', 'nationalid', 'dateofbirth', 'phone'];
       if (!requiredHeaders.every(h => header.includes(h))) {
        throw new Error(`Invalid headers. Required headers are: ${requiredHeaders.join(', ')}`);
      }
      
      // FIX: Corrected object creation to match the Customer model, including the required 'storeId'.
      const customersToImport: Omit<Customer, 'id' | 'createdAt'>[] = lines
        .filter(l => l.trim().length > 0)
        .map(line => {
            const values = this.parseCsvLine(line);
            const row = header.reduce((obj, col, index) => {
                obj[col] = values[index]?.trim() ?? '';
                return obj;
            }, {} as any);

            return {
                storeId,
                fullName: row.name,
                citizenId: row.nationalid,
                birthDate: row.dateofbirth,
                phone: row.phone,
                pointsBalance: 0,
                tierLevel: 'Bronze'
            };
      });
      
      const result = await this.dataService.batchAddCustomers(customersToImport);
       if (result.success) {
        this.customerStatus.set({ type: 'success', message: result.message });
        this.customersCsv.set('');
      } else {
        this.customerStatus.set({ type: 'error', message: `Import failed: ${result.message}` });
      }

    } catch (e) {
      const message = e instanceof Error ? e.message : 'An unknown error occurred';
      this.customerStatus.set({ type: 'error', message });
    } finally {
      this.isImportingCustomers.set(false);
    }
  }
}
